import React, { useState, useMemo } from 'react';
import { 
  Plus, 
  Store, 
  Users, 
  Clock, 
  Package, 
  TrendingUp, 
  Eye,
  Edit,
  Trash2,
  Download,
  Filter,
  X
} from 'lucide-react';

// Synthetic revenue datasets by period
const revenueDataByPeriod = {
  week: [3200, 4100, 2800, 3600, 4200, 3900, 4500],
  month: [12000, 14500, 13200, 16000, 15500, 17000, 18000, 15000, 16200, 17500, 16800, 19000],
  quarter: [42000, 51000, 47000],
  year: [520000]
};

const revenueLabelsByPeriod = {
  week: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  month: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  quarter: ['Q1', 'Q2', 'Q3'],
  year: ['2025']
};

const Dashboard = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [showViewAllCompanies, setShowViewAllCompanies] = useState(false);
  const [showViewAllExpired, setShowViewAllExpired] = useState(false);
  const [showViewAllTopPerforming, setShowViewAllTopPerforming] = useState(false);
  const [showCompanyViewModal, setShowCompanyViewModal] = useState(false);
  const [showCompanyEditModal, setShowCompanyEditModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedCompanySource, setSelectedCompanySource] = useState(null); // 'recent' | 'expired' | 'top'
  const [editCompany, setEditCompany] = useState({ name: '', email: '', package: '', users: 0, status: 'Active' });
  
  // Form state for adding company
  const [newCompany, setNewCompany] = useState({
    name: '',
    email: '',
    package: ''
  });

  const revenueData = useMemo(() => revenueDataByPeriod[selectedPeriod] || [], [selectedPeriod]);
  const revenueLabels = useMemo(() => revenueLabelsByPeriod[selectedPeriod] || [], [selectedPeriod]);

  const maxRevenue = Math.max(...revenueData, 1);

  const [statsData, setStatsData] = useState([
    {
      title: 'Total Companies',
      value: '1,234',
      change: '+12%',
      changeType: 'positive',
      icon: Store,
      iconColor: 'bg-blue-100 text-blue-600',
      description: 'from last month'
    },
    {
      title: 'Active Companies',
      value: '987',
      change: '+8%',
      changeType: 'positive',
      icon: Users,
      iconColor: 'bg-green-100 text-green-600',
      description: '80% active rate'
    },
    {
      title: 'License Expired',
      value: '45',
      change: '-5%',
      changeType: 'negative',
      icon: Clock,
      iconColor: 'bg-red-100 text-red-600',
      description: 'requires attention'
    },
    {
      title: 'Total Packages',
      value: '12',
      change: '+2',
      changeType: 'positive',
      icon: Package,
      iconColor: 'bg-purple-100 text-purple-600',
      description: 'available plans'
    }
  ]);

  const [recentCompanies, setRecentCompanies] = useState([
    { id: 1, name: 'Beauty Haven', email: 'contact@beautyhaven.com', status: 'Active', date: '2025-01-15', package: 'Professional', users: 5 },
    { id: 2, name: 'Style Studio', email: 'info@stylestudio.com', status: 'Pending', date: '2025-01-14', package: 'Starter', users: 2 },
    { id: 3, name: 'Glamour Salon', email: 'hello@glamoursalon.com', status: 'Active', date: '2025-01-13', package: 'Enterprise', users: 8 },
    { id: 4, name: 'Elegance Spa', email: 'contact@elegancespa.com', status: 'Active', date: '2025-01-12', package: 'Professional', users: 4 },
    { id: 5, name: 'Modern Cuts', email: 'info@moderncuts.com', status: 'Active', date: '2025-01-11', package: 'Starter', users: 2 }
  ]);

  const [expiredLicenses, setExpiredLicenses] = useState([
    { id: 1, name: 'Classic Cuts', email: 'admin@classiccuts.com', expired: '2025-01-10', users: 5, package: 'Professional' },
    { id: 2, name: 'Modern Styles', email: 'info@modernstyles.com', expired: '2025-01-08', users: 3, package: 'Starter' },
    { id: 3, name: 'Trendy Hair', email: 'hello@trendyhair.com', expired: '2025-01-05', users: 7, package: 'Enterprise' },
    { id: 4, name: 'Elite Salon', email: 'contact@elitesalon.com', expired: '2025-01-03', users: 4, package: 'Professional' }
  ]);

  const [topPerformingCompanies, setTopPerformingCompanies] = useState([
    { name: 'Glamour Salon', revenue: '$12,450', growth: '+23%', users: 8, status: 'Active' },
    { name: 'Beauty Haven', revenue: '$8,920', growth: '+18%', users: 5, status: 'Active' },
    { name: 'Elegance Spa', revenue: '$7,340', growth: '+15%', users: 4, status: 'Active' },
    { name: 'Style Studio', revenue: '$5,670', growth: '+12%', users: 2, status: 'Active' }
  ]);

  const handleAddCompany = () => {
    setShowAddCompanyModal(true);
  };

  const handleExportData = () => {
    // Export functionality
    console.log('Exporting dashboard data...');
    alert('Export functionality would download dashboard data as CSV/Excel');
  };

  const handleRenewLicense = (companyId) => {
    console.log('Renewing license for company:', companyId);
    // Remove from expired list and update stats
    setExpiredLicenses(prev => prev.filter(company => company.id !== companyId));
    
    // Update stats
    setStatsData(prev => prev.map(stat => 
      stat.title === 'License Expired' 
        ? { ...stat, value: String(parseInt(stat.value) - 1), change: '-1', changeType: 'positive' }
        : stat
    ));
    
    alert(`License renewed for company ID: ${companyId}`);
  };

  const handleSubmitNewCompany = (e) => {
    e.preventDefault();
    if (!newCompany.name || !newCompany.email || !newCompany.package) {
      alert('Please fill in all fields');
      return;
    }

    const newCompanyData = {
      id: Date.now(),
      name: newCompany.name,
      email: newCompany.email,
      status: 'Active',
      date: new Date().toISOString().split('T')[0],
      package: newCompany.package,
      users: 1
    };

    setRecentCompanies(prev => [newCompanyData, ...prev.slice(0, 4)]);
    
    // Update stats
    setStatsData(prev => prev.map(stat => 
      stat.title === 'Total Companies' 
        ? { ...stat, value: String(parseInt(stat.value.replace(',', '')) + 1) }
        : stat.title === 'Active Companies'
        ? { ...stat, value: String(parseInt(stat.value) + 1) }
        : stat
    ));

    // Reset form and close modal
    setNewCompany({ name: '', email: '', package: '' });
    setShowAddCompanyModal(false);
    
    alert('Company added successfully!');
  };

  const openViewCompany = (company, source) => {
    setSelectedCompanySource(source);
    setSelectedCompany(company);
    setShowCompanyViewModal(true);
  };

  const openEditCompany = (company, source) => {
    setSelectedCompanySource(source);
    setSelectedCompany(company);
    // Seed edit form with available fields
    setEditCompany({
      name: company.name || '',
      email: company.email || '',
      package: company.package || '',
      users: company.users ?? 0,
      status: company.status || 'Active'
    });
    setShowCompanyEditModal(true);
  };

  const saveEditedCompany = (e) => {
    e.preventDefault();
    if (!selectedCompany) return;
    const updated = { ...selectedCompany, ...editCompany };

    if (selectedCompanySource === 'recent') {
      setRecentCompanies(prev => prev.map(c => c.id === selectedCompany.id ? updated : c));
    } else if (selectedCompanySource === 'expired') {
      setExpiredLicenses(prev => prev.map(c => c.id === selectedCompany.id ? updated : c));
    } else if (selectedCompanySource === 'top') {
      setTopPerformingCompanies(prev => prev.map(c => c.name === selectedCompany.name ? { ...c, name: updated.name, users: updated.users, status: updated.status } : c));
    }

    setShowCompanyEditModal(false);
    setSelectedCompany(null);
  };

  const handleDeleteCompany = (companyId) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      setTopPerformingCompanies(prev => prev.filter(company => company.name !== companyId));
      alert('Company deleted successfully!');
    }
  };

  const handleViewAll = (type) => {
    switch(type) {
      case 'companies':
        setShowViewAllCompanies(true);
        break;
      case 'expired':
        setShowViewAllExpired(true);
        break;
      case 'topPerforming':
        setShowViewAllTopPerforming(true);
        break;
      default:
        break;
    }
  };

  // Chart hover functionality
  React.useEffect(() => {
    const svg = document.querySelector('#revenue-chart svg');
    const tooltip = document.getElementById('chart-tooltip');
    const tooltipLabel = document.getElementById('tooltip-label');
    const tooltipValue = document.getElementById('tooltip-value');

    if (!svg || !tooltip || !tooltipLabel || !tooltipValue) return;

    const handleMouseMove = (e) => {
      const rect = svg.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Convert to SVG coordinates
      const svgPoint = svg.createSVGPoint();
      svgPoint.x = x;
      svgPoint.y = y;
      const transformedPoint = svgPoint.matrixTransform(svg.getScreenCTM().inverse());
      
      // Find which bar we're hovering over
      const barWidth = 90 / Math.max(revenueData.length, 1);
      
      const barIndex = Math.floor((transformedPoint.x - 5) / barWidth);
      
      if (barIndex >= 0 && barIndex < revenueData.length) {
        const value = revenueData[barIndex];
        const label = revenueLabels[barIndex];
        
        tooltipLabel.textContent = label;
        tooltipValue.textContent = `$${value.toLocaleString()}`;
        
        tooltip.style.display = 'block';
        
        // Position tooltip with bounds checking
        const tooltipRect = tooltip.getBoundingClientRect();
        const chartRect = rect;
        
        let tooltipX = x;
        let tooltipY = y - 10;
        
        // Adjust X position to keep tooltip within chart bounds
        if (tooltipX + tooltipRect.width / 2 > chartRect.width) {
          tooltipX = chartRect.width - tooltipRect.width / 2;
        } else if (tooltipX - tooltipRect.width / 2 < 0) {
          tooltipX = tooltipRect.width / 2;
        }
        
        // Adjust Y position to keep tooltip above the cursor
        if (tooltipY - tooltipRect.height < 0) {
          tooltipY = y + 20; // Show below cursor if not enough space above
        }
        
        tooltip.style.left = `${tooltipX}px`;
        tooltip.style.top = `${tooltipY}px`;
      } else {
        tooltip.style.display = 'none';
      }
    };

    const handleMouseLeave = () => {
      tooltip.style.display = 'none';
    };

    // Mouse events
    svg.addEventListener('mousemove', handleMouseMove);
    svg.addEventListener('mouseleave', handleMouseLeave);
    
    // Touch events for mobile
    svg.addEventListener('touchstart', handleMouseMove);
    svg.addEventListener('touchmove', handleMouseMove);
    svg.addEventListener('touchend', handleMouseLeave);

    return () => {
      svg.removeEventListener('mousemove', handleMouseMove);
      svg.removeEventListener('mouseleave', handleMouseLeave);
      svg.removeEventListener('touchstart', handleMouseMove);
      svg.removeEventListener('touchmove', handleMouseMove);
      svg.removeEventListener('touchend', handleMouseLeave);
    };
  }, [revenueData, revenueLabels]);

  return (
    <div className="p-4 lg:p-6 space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-800">Super Admin Dashboard</h1>
          <p className="text-gray-600">Monitor and manage all salon companies</p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
          <select 
            value={selectedPeriod} 
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
          </select>
          <button 
            onClick={handleExportData}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button 
            onClick={handleAddCompany}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Company</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {statsData.map((stat, index) => (
          <div key={index} className="bg-white p-4 lg:p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stat.value}</p>
                <div className="flex flex-col sm:flex-row sm:items-center mt-1 space-y-1 sm:space-y-0">
                  <span className={`text-sm flex items-center ${
                    stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    <TrendingUp className={`w-4 h-4 mr-1 ${stat.changeType === 'negative' ? 'rotate-180' : ''}`} />
                    {stat.change}
                  </span>
                  <span className="text-sm text-gray-500 sm:ml-2">{stat.description}</span>
                </div>
              </div>
              <div className={`p-2 lg:p-3 rounded-full ${stat.iconColor} flex-shrink-0 ml-3`}>
                <stat.icon className="w-6 h-6 lg:w-8 lg:h-8" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Revenue Overview (Bar Chart) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 lg:p-6 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
          <h3 className="text-lg font-semibold text-gray-800">Revenue Overview</h3>
          <div className="text-sm text-gray-500">Period: {selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1)}</div>
        </div>
        <div className="p-4 lg:p-6">
          <div id="revenue-chart" className="w-full h-64 relative">
            {/* Simple responsive SVG bar chart */}
            <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-full">
              {/* Axes */}
              <line x1="5" y1="35" x2="98" y2="35" stroke="#E5E7EB" strokeWidth="0.5" />
              <line x1="5" y1="5" x2="5" y2="35" stroke="#E5E7EB" strokeWidth="0.5" />
              
              {/* Y-axis labels */}
              {[1, 2, 3, 4].map((i) => {
                const yValue = (i * maxRevenue / 4).toLocaleString();
                return (
                  <g key={i}>
                    <line x1="5" y1={35 - (i * 7)} x2="98" y2={35 - (i * 7)} stroke="#F3F4F6" strokeWidth="0.4" />
                    <text x="3" y={35 - (i * 7) + 0.5} textAnchor="end" fontSize="1.5" fill="#6B7280">
                      ${yValue}
                    </text>
                  </g>
                );
              })}
              
              {/* Bars */}
              {revenueData.map((value, idx) => {
                const barWidth = 90 / Math.max(revenueData.length, 1);
                const gap = barWidth * 0.2;
                const usableWidth = barWidth - gap;
                const height = (value / maxRevenue) * 28; // 28px max bar height in viewBox units
                const x = 5 + idx * barWidth + gap / 2;
                const y = 35 - height;
                return (
                  <g key={idx}>
                    <rect
                      x={x}
                      y={y}
                      width={usableWidth}
                      height={height}
                      rx="0.8"
                      fill="#16A34A"
                      className="cursor-pointer transition-all duration-200 hover:fill-green-500 hover:stroke-green-600 hover:stroke-2 hover:shadow-lg"
                      data-value={value}
                      data-label={revenueLabels[idx]}
                      data-index={idx}
                      style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }}
                    />
                    {/* Value label on top of bar */}
                    <text 
                      x={x + usableWidth / 2} 
                      y={Math.max(y - 2, 6)} 
                      textAnchor="middle" 
                      fontSize="1.8" 
                      fill="#16A34A" 
                      className="font-semibold"
                      style={{ textShadow: '0 1px 2px rgba(255,255,255,0.8)' }}
                    >
                      ${value.toLocaleString()}
                    </text>
                    {/* X-axis label */}
                    <text x={x + usableWidth / 2} y={38} textAnchor="middle" fontSize="2" fill="#6B7280">
                      {revenueLabels[idx] || ''}
                    </text>
                  </g>
                );
              })}
            </svg>
            
            {/* Hover Tooltip */}
            <div 
              id="chart-tooltip"
              className="absolute hidden bg-gray-900 text-white text-sm px-3 py-2 rounded-lg shadow-lg pointer-events-none z-10 min-w-max border border-gray-700"
              style={{
                transform: 'translate(-50%, -100%)',
                marginTop: '-10px'
              }}
            >
              <div className="font-semibold text-center text-xs text-gray-300 uppercase tracking-wide mb-1" id="tooltip-label"></div>
              <div className="text-green-300 text-center font-mono text-lg font-bold" id="tooltip-value"></div>
            </div>
          </div>
          
          {/* Chart Summary */}
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span>Total: <span className="font-semibold text-gray-800">${revenueData.reduce((sum, val) => sum + val, 0).toLocaleString()}</span></span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              <span>Average: <span className="font-semibold text-gray-800">${Math.round(revenueData.reduce((sum, val) => sum + val, 0) / revenueData.length).toLocaleString()}</span></span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-300 rounded-full"></div>
              <span>Highest: <span className="font-semibold text-gray-800">${Math.max(...revenueData).toLocaleString()}</span></span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts and Tables Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6">
        {/* Recent Companies */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <div className="p-4 lg:p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">Recent Companies</h3>
            <button 
              onClick={() => handleViewAll('companies')}
              className="text-green-600 hover:text-green-700 text-sm font-medium"
            >
              View All
            </button>
          </div>
        </div>
        <div className="p-4 lg:p-6">
            <div className="space-y-4">
              {recentCompanies.map((company) => (
                <div key={company.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors space-y-3 sm:space-y-0">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-green-600 font-semibold">{company.name.charAt(0)}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">{company.name}</p>
                      <p className="text-sm text-gray-500 truncate">{company.email}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-xs text-gray-500">{company.package}</span>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs text-gray-500">{company.users} users</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      company.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {company.status}
                    </span>
                    <div className="flex space-x-1">
                      <button 
                        onClick={() => openViewCompany(company, 'recent')}
                        className="p-1 text-blue-600 hover:text-blue-800"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => openEditCompany(company, 'recent')}
                        className="p-1 text-green-600 hover:text-green-800"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Expired Licenses */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <div className="p-4 lg:p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">License Expired</h3>
            <button 
              onClick={() => handleViewAll('expired')}
              className="text-red-600 hover:text-red-700 text-sm font-medium"
            >
              View All
            </button>
          </div>
        </div>
        <div className="p-4 lg:p-6">
            <div className="space-y-4">
              {expiredLicenses.map((company) => (
                <div key={company.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <span className="text-red-600 font-semibold">{company.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{company.name}</p>
                      <p className="text-sm text-red-600">Expired: {company.expired}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-xs text-gray-500">{company.package}</span>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs text-gray-500">{company.users} users</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={() => handleRenewLicense(company.id)}
                      className="px-3 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Renew
                    </button>
                    <div className="flex space-x-1">
                      <button 
                        onClick={() => openViewCompany(company, 'expired')}
                        className="p-1 text-blue-600 hover:text-blue-800"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => openEditCompany(company, 'expired')}
                        className="p-1 text-green-600 hover:text-green-800"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Top Performing Companies */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 lg:p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <h3 className="text-lg font-semibold text-gray-800">Top Performing Companies</h3>
            <div className="flex items-center space-x-2">
              <button className="p-2 text-gray-400 hover:text-gray-600">
                <Filter className="w-4 h-4" />
              </button>
              <button 
                onClick={() => handleViewAll('topPerforming')}
                className="text-green-600 hover:text-green-700 text-sm font-medium"
              >
                View All
              </button>
            </div>
          </div>
        </div>
        <div className="p-4 lg:p-6">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Company</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Revenue</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Growth</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Users</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {topPerformingCompanies.map((company, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <span className="text-green-600 font-semibold text-sm">{company.name.charAt(0)}</span>
                        </div>
                        <span className="font-medium text-gray-900">{company.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-700 font-medium">{company.revenue}</td>
                    <td className="py-3 px-4">
                      <span className="text-green-600 font-medium">{company.growth}</span>
                    </td>
                    <td className="py-3 px-4 text-gray-700">{company.users}</td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {company.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex space-x-2">
                                                  <button 
                            onClick={() => openViewCompany(company, 'top')}
                            className="p-1 text-blue-600 hover:text-blue-800"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => openEditCompany(company, 'top')}
                            className="p-1 text-green-600 hover:text-green-800"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        <button 
                          onClick={() => handleDeleteCompany(company.name)}
                          className="p-1 text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add Company Modal */}
      {showAddCompanyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 lg:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Add New Company</h3>
              <button 
                onClick={() => setShowAddCompanyModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmitNewCompany} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                <input 
                  type="text" 
                  value={newCompany.name}
                  onChange={(e) => setNewCompany({...newCompany, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Enter company name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input 
                  type="email" 
                  value={newCompany.email}
                  onChange={(e) => setNewCompany({...newCompany, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Enter email address"
                  required
        />
      </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Package</label>
                <select 
                  value={newCompany.package}
                  onChange={(e) => setNewCompany({...newCompany, package: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                >
                  <option value="">Select a package</option>
                  <option value="Starter">Starter</option>
                  <option value="Professional">Professional</option>
                  <option value="Enterprise">Enterprise</option>
                </select>
              </div>
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                <button 
                  type="button"
                  onClick={() => setShowAddCompanyModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Add Company
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View All Companies Modal */}
      {showViewAllCompanies && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 lg:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">All Companies</h3>
              <button 
                onClick={() => setShowViewAllCompanies(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              {recentCompanies.map((company) => (
                <div key={company.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 font-semibold">{company.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{company.name}</p>
                      <p className="text-sm text-gray-500">{company.email}</p>
                      <p className="text-xs text-gray-400">Added: {company.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      company.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {company.status}
                    </span>
                    <span className="text-xs text-gray-500">{company.package}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* View All Expired Licenses Modal */}
      {showViewAllExpired && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 lg:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">All Expired Licenses</h3>
              <button 
                onClick={() => setShowViewAllExpired(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              {expiredLicenses.map((company) => (
                <div key={company.id} className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <span className="text-red-600 font-semibold">{company.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{company.name}</p>
                      <p className="text-sm text-red-600">Expired: {company.expired}</p>
                      <p className="text-xs text-gray-400">{company.package} • {company.users} users</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => handleRenewLicense(company.id)}
                    className="px-3 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Renew License
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* View All Top Performing Companies Modal */}
      {showViewAllTopPerforming && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 lg:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">All Top Performing Companies</h3>
              <button 
                onClick={() => setShowViewAllTopPerforming(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Company</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Revenue</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Growth</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Users</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {topPerformingCompanies.map((company, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <span className="text-green-600 font-semibold text-sm">{company.name.charAt(0)}</span>
                          </div>
                          <span className="font-medium text-gray-900">{company.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-700 font-medium">{company.revenue}</td>
                      <td className="py-3 px-4">
                        <span className="text-green-600 font-medium">{company.growth}</span>
                      </td>
                      <td className="py-3 px-4 text-gray-700">{company.users}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {company.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => openViewCompany(company, 'top')}
                            className="p-1 text-blue-600 hover:text-blue-800"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => openEditCompany(company, 'top')}
                            className="p-1 text-green-600 hover:text-green-800"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleDeleteCompany(company.name)}
                            className="p-1 text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Company View Modal */}
      {showCompanyViewModal && selectedCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 lg:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Company Details</h3>
              <button onClick={() => setShowCompanyViewModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Name</span><span className="font-medium text-gray-800">{selectedCompany.name}</span></div>
              {selectedCompany.email && (
                <div className="flex justify-between"><span className="text-gray-500">Email</span><span className="font-medium text-gray-800">{selectedCompany.email}</span></div>
              )}
              {selectedCompany.package && (
                <div className="flex justify-between"><span className="text-gray-500">Package</span><span className="font-medium text-gray-800">{selectedCompany.package}</span></div>
              )}
              {typeof selectedCompany.users !== 'undefined' && (
                <div className="flex justify-between"><span className="text-gray-500">Users</span><span className="font-medium text-gray-800">{selectedCompany.users}</span></div>
              )}
              {selectedCompany.status && (
                <div className="flex justify-between"><span className="text-gray-500">Status</span><span className="font-medium text-gray-800">{selectedCompany.status}</span></div>
              )}
              {selectedCompany.date && (
                <div className="flex justify-between"><span className="text-gray-500">Added</span><span className="font-medium text-gray-800">{selectedCompany.date}</span></div>
              )}
              {selectedCompany.expired && (
                <div className="flex justify-between"><span className="text-gray-500">Expired</span><span className="font-medium text-gray-800">{selectedCompany.expired}</span></div>
              )}
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={() => setShowCompanyViewModal(false)} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Company Edit Modal */}
      {showCompanyEditModal && selectedCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 lg:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Edit Company</h3>
              <button onClick={() => setShowCompanyEditModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={saveEditedCompany} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input value={editCompany.name} onChange={(e) => setEditCompany({ ...editCompany, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
              </div>
              {typeof selectedCompany.email !== 'undefined' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input value={editCompany.email} onChange={(e) => setEditCompany({ ...editCompany, email: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
                </div>
              )}
              {typeof selectedCompany.package !== 'undefined' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Package</label>
                  <select value={editCompany.package} onChange={(e) => setEditCompany({ ...editCompany, package: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
                    <option value="Starter">Starter</option>
                    <option value="Professional">Professional</option>
                    <option value="Enterprise">Enterprise</option>
                  </select>
                </div>
              )}
              {typeof selectedCompany.users !== 'undefined' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Users</label>
                  <input type="number" min="0" value={editCompany.users} onChange={(e) => setEditCompany({ ...editCompany, users: Number(e.target.value) })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
                </div>
              )}
              {typeof selectedCompany.status !== 'undefined' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select value={editCompany.status} onChange={(e) => setEditCompany({ ...editCompany, status: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
                    <option value="Active">Active</option>
                    <option value="Pending">Pending</option>
                  </select>
                </div>
              )}
              <div className="pt-2 flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                <button type="button" onClick={() => setShowCompanyEditModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
